function milesToKm(miles, decimals = 2) {
    const KM_PER_MILE = 1.60934;
    const km = miles * KM_PER_MILE;
    return Math.round(km * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

class StoreLocator extends HTMLElement {
    constructor() {
        super();
        if (!window.platformScripts.api.mapbox) return;
        mapboxgl.accessToken = window.platformScripts.api.mapbox;
        this.geocoderContainer = this.querySelector('#geocoder-container');
        this.method = 'manual';

        this.dataMapZoom = this.getAttribute('data-map-zoom') ?? 5.5;
        this.dataMapLongitude = parseFloat(this.getAttribute('data-map-long')) ?? -6.24827;
        this.dataMapLatitude = parseFloat(this.getAttribute('data-map-lat')) ?? 53.34807;
        this.dataMapDistanceUnit = this.getAttribute('data-distance');
        this.allMarkers = Array.isArray(window.theme.store_locator.additionalLocationMarkers)
            ? window.theme.store_locator.markers.concat(window.theme.store_locator.additionalLocationMarkers)
            : window.theme.store_locator.markers;
        this.selectedMarker = null;

        this.idCounter = 0;
        this.mapFeatures = [];
        this.selectedFeatureId = null;
        this.errorMessage = this.querySelector('.global-message');
        this.errorMessageContent = this.querySelector('.global-message__copy');
        this.userCoords = null;

        this.enableCurrentLocationButton = this.getAttribute('data-enable-current-location-button') === 'true';

        if (this.enableCurrentLocationButton) {
            this.querySelector('button[name="current-location"]').addEventListener(
                'click',
                this.getUserCurrentLocation.bind(this)
            );
        }
    }

    connectedCallback() {
        this.activeContainer = this.getActiveContainer();

        this.map = new mapboxgl.Map({
            container: 'map',
            style: 'mapbox://styles/mapbox/light-v11',
            center: [this.dataMapLongitude, this.dataMapLatitude],
            zoom: this.dataMapZoom,
        });

        this.geocoder = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl: mapboxgl,
            marker: false,
            flyTo: false,
            container: 'geocoder-container',
            countries: Shopify.country,
        });

        if (!this.allMarkers) return;

        this.map.on('load', this.mapLoad.bind(this));

        this.geocoderContainer.appendChild(this.geocoder.onAdd(this.map));

        this.renderChanges();

        this.geocoder.on('results', this.renderResults.bind(this));
        this.activeContainer.addEventListener('click', this.resultsContainer.bind(this));

        let debounceTimer;
        const currentThis = this;
        window.addEventListener('resize', function () {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const currentActiveContainer = currentThis.getActiveContainer();
                if (currentActiveContainer !== currentThis.activeContainer) {
                    currentThis.transferResults();
                    currentThis.activeContainer = currentActiveContainer;
                }
            }, 100);
        });
    }

    renderChanges() {
        this.querySelector('.mapboxgl-ctrl-geocoder--input').placeholder =
            window.storeLocatorStrings.townPostalCodeText;
        const searchIcon = this.querySelector('.mapboxgl-ctrl-geocoder--icon-search');
        const newSearchIcon = new DOMParser()
            .parseFromString(window.theme.store_locator.searchIcon, 'text/html')
            .querySelector('body').firstChild;
        newSearchIcon.classList.add(...['mapboxgl-ctrl-geocoder--icon', 'mapboxgl-ctrl-geocoder--icon-search']);
        searchIcon.parentElement.prepend(newSearchIcon);
        searchIcon.remove();
    }

    getActiveContainer() {
        return window.innerWidth > 1024
            ? document.getElementById('search-results')
            : document.getElementById('search-results-mobile');
    }

    getUserCurrentLocation() {
        const currentThis = this;
        this.method = 'location';

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    currentThis.userCoords = [position.coords.longitude, position.coords.latitude];
                    const nearbyLocations = currentThis.findNearbyLocations(currentThis.userCoords);
                    currentThis.displaySearchResults(nearbyLocations, currentThis.userCoords);
                },
                function (error) {
                    console.log('error');
                    if (error.code === error.PERMISSION_DENIED) {
                        currentThis.errorMessageContent.textContent = window.storeLocatorStrings.locationDenied;
                        currentThis.errorMessage.classList.add('global-message--active');
                    } else {
                        console.error('Error occurred: ' + error.message);
                    }
                }
            );
        } else {
            console.error('Geolocation is not supported by this browser.');
        }
    }

    renderResults(results) {
        if (!results.features.length) {
            sendAnalyticsEventData(USER_INTERACTION_ANALYTICS_EVENTS.storeLocator, {
                event: 'Store Locator search',
                searchedstore: 'no store found',
            });
            return;
        }

        const firstResult = results.features[0];
        this.map.flyTo({
            center: firstResult.geometry.coordinates,
            zoom: 14,
        });
        this.userCoords = firstResult.geometry.coordinates;
        const nearbyLocations = this.findNearbyLocations(firstResult.geometry.coordinates);
        this.displaySearchResults(nearbyLocations, firstResult.geometry.coordinates);

        sendAnalyticsEventData(USER_INTERACTION_ANALYTICS_EVENTS.storeLocator, {
            event: 'StoreLocatorSearch',
            searchedstore: this.geocoder.inputString,
            storeSearchResults: nearbyLocations.length,
        });
    }

    resultsContainer() {
        const toggleDiv = event.target.closest('.store-item-toggle');
        if (!toggleDiv) return;

        const storeItem = toggleDiv.closest('.store-item');
        if (!storeItem) return;

        const content = storeItem.querySelector('.content');

        if (storeItem.classList.contains('active')) {
            content.style.display = 'none';
            storeItem.classList.remove('active');
            if (this.currentPopup) {
                this.currentPopup.remove();
            }
            this.removeSelectedMarker();
        } else {
            this.closeAllStoreItems();
            storeItem.classList.add('active');
            const lat = parseFloat(storeItem.getAttribute('data-lat'));
            const lng = parseFloat(storeItem.getAttribute('data-lng'));
            const distanceFromSearch = parseFloat(storeItem.getAttribute('data-distance-from-search'));
            const coords = [lng, lat];

            this.map.flyTo({ center: coords });
            this.triggerLocationPopup(coords);
            content.style.display = 'block';
            if (this.selectedMarker) {
                this.selectedMarker.remove();
            }
            const markerElement = this.createCustomMarker();
            this.selectedMarker = new mapboxgl.Marker(markerElement).setLngLat(coords).addTo(this.map);

            sendAnalyticsEventData(USER_INTERACTION_ANALYTICS_EVENTS.storeLocator, {
                event: 'StoreLocatorUseLocation',
                searchedstore: this.geocoder.inputString,
                getDirections: 'yes',
                distance: `${milesToKm(distanceFromSearch)}km`,
            });
        }
    }

    mapLoad() {
        this.allMarkers.forEach((marker) => {
            marker.id = this.idCounter++;
            this.mapFeatures.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [marker.Longitude, marker.Latitude],
                },
                properties: {
                    id: marker.id,
                    name: marker.Name,
                    addressLine1: marker['Address Line 1'],
                    addressLine2: marker['Address Line 2'],
                    city: marker['City/Town'],
                    postalCode: marker['Postal Code'],
                },
            });
        });

        this.map.addSource('stores', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: this.mapFeatures,
            },
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
            generateId: true,
        });

        this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'stores',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#01567e', 100, '#01567e', 750, '#01567e'],
                'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
                'circle-stroke-color': 'lightgray',
                'circle-stroke-width': 4,
            },
        });

        this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'stores',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
            paint: {
                'text-color': '#ffffff',
            },
        });

        this.map.loadImage(window.theme.store_locator.markerImage, (error, image) => {
            if (error) throw error;
            this.map.addImage('vuseIcon', image);
        });

        this.map.loadImage(window.theme.store_locator.markerImageSelected, (error, selectedImage) => {
            if (error) throw error;
            this.map.addImage('vuseIconSelected', selectedImage);
        });

        this.map.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: 'stores',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': 'vuseIcon',
                'icon-size': 0.25,
            },
        });

        this.map.addLayer({
            id: 'unclustered-point-selected',
            type: 'symbol',
            source: 'stores',
            filter: ['==', ['id'], this.selectedFeatureId],
            layout: {
                'icon-image': 'vuseIconSelected',
                'icon-size': 0.25,
            },
        });

        this.map.on('click', 'clusters', (e) => {
            const features = this.map.queryRenderedFeatures(e.point, {
                layers: ['clusters'],
            });
            const clusterId = features[0].properties.cluster_id;
            this.map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;

                this.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                });
            });
        });

        this.map.on('click', 'unclustered-point', (e) => {
            const coordinates = e.features[0].geometry.coordinates.slice();
            const { name, addressLine1, addressLine2, city, postalCode } = e.features[0].properties;
            let distance = 0;
            if (this.userCoords) {
                distance = this.calculateDistance(this.userCoords, [coordinates[0], coordinates[1]]);
            }

            const googleMapsURL = `https://www.google.com/maps/dir/?api=1&destination=${coordinates[1]},${coordinates[0]}`;

            const addressHTML = `
                <div class="location-popup">
                    <strong>${name}</strong><br>
                    <p>
                        ${addressLine1},
                        ${addressLine2 ? `${addressLine2}, ` : ``}
                        ${city},
                        ${postalCode}
                    </p>
                    <button class="button button--primary button--small button--full-width">
                        ${window.storeLocatorStrings.direction}
                    </button>
                </div>
            `;

            const popup = new mapboxgl.Popup({ offset: [0, -20] })
                .setLngLat(coordinates)
                .setHTML(addressHTML)
                .addTo(this.map);

            popup.on('close', () => {
                this.selectedFeatureId = null;
                this.map.setFilter('unclustered-point', ['!', ['has', 'point_count']]);
                this.map.setFilter('unclustered-point-selected', ['==', ['id'], -1]);
                this.removeSelectedMarker();
            });

            this.selectedFeatureId = e.features[0].id;
            this.map.setFilter('unclustered-point', [
                'all',
                ['!', ['has', 'point_count']],
                ['!=', ['id'], this.selectedFeatureId],
            ]);
            this.map.setFilter('unclustered-point-selected', ['==', ['id'], this.selectedFeatureId]);
        });

        this.map.on('click', (e) => {
            if (e.features && e.features.length > 0) {
                const clickedOnPoint = e.features.some((feature) => feature.layer.id === 'unclustered-point');
                if (!clickedOnPoint) {
                    this.selectedFeatureId = null;
                    this.map.setFilter('unclustered-point', ['!', ['has', 'point_count']]);
                    this.map.setFilter('unclustered-point-selected', ['==', ['id'], -1]);
                }
            }
        });
    }

    displaySearchResults(locations, userCoords) {
        const currentThis = this;
        const resultsContainer = this.getActiveContainer();
        const fragment = document.createDocumentFragment();
        resultsContainer.innerHTML = '';

        if (!locations.length) {
            const noResults = document.createElement('p');
            noResults.textContent = window.storeLocatorStrings.error;
            noResults.style.color = '#991f39';
            noResults.style.lineHeight = '1.2';
            fragment.appendChild(noResults);
        } else {
            locations.forEach((location) => {
                const googleMapsURL = `https://www.google.com/maps/dir/?api=1&destination=${location.Latitude},${location.Longitude}`;
                const distance = this.calculateDistance(userCoords, [location.Longitude, location.Latitude]);
                const resultDiv = document.createElement('div');

                resultDiv.classList.add('store-item');
                resultDiv.dataset.lng = location.Longitude;
                resultDiv.dataset.lat = location.Latitude;
                resultDiv.dataset.distanceFromSearch = location.distanceFromSearch;
                resultDiv.dataset.id = location.id;
                resultDiv.innerHTML = `
                    <div class="store-item-toggle">
                        <div class="title-wrapper">
                            <h4 class="title">${location.Name}</h4>
                        </div>
                        <p>
                        ${distance.toFixed(2)} ${
                    currentThis.dataMapDistanceUnit === 'miles'
                        ? window.storeLocatorStrings.distanceUnitMiles
                        : window.storeLocatorStrings.distanceUnitKm
                }
                            ${window.storeLocatorStrings.distanceText}
                        </p>
                    </div>
                    <div class="content">
                        <p class="address">
                            ${location['Address Line 1']}
                            ${location['Address Line 2'] ? ', ' + location['Address Line 2'] : ''}
                            ${location['City/Town']},
                            ${location['Postal Code']}
                        </p>
                        <div class="store-locator-products">
                            <p>${window.storeLocatorStrings.availableProductText}</p>
                        </div>
                        <div>
                            <a href="${googleMapsURL}" class="button button--secondary" target="_blank">
                                ${window.storeLocatorStrings.direction}
                            </a>
                        </div>
                    </div>
                `;

                fragment.appendChild(resultDiv);
            });
        }
        resultsContainer.appendChild(fragment);
    }

    findNearbyLocations(coords) {
        const currentThis = this;
        const maxDistance = this.dataMapDistanceUnit === 'miles' ? 10 : 16.09;
        const nearbyLocations = this.allMarkers.filter((marker) => {
            const distance = currentThis.calculateDistance(coords, [marker.Longitude, marker.Latitude]);
            marker.distanceFromSearch = distance;
            return distance <= maxDistance;
        });
        nearbyLocations.sort((a, b) => a.distanceFromSearch - b.distanceFromSearch);
        return nearbyLocations;
    }

    createCustomMarker() {
        const el = document.createElement('div');
        el.style.width = '35px';
        el.style.height = '40px';
        el.style.backgroundImage = `url(${window.theme.store_locator.markerImageSelected})`;
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        return el;
    }

    removeSelectedMarker() {
        if (this.selectedMarker) {
            this.selectedMarker.remove();
            this.selectedMarker = null;
        }
    }

    triggerLocationPopup(coords) {
        const currentThis = this;
        if (this.currentPopup) {
            this.currentPopup.remove();
        }
        this.removeSelectedMarker();

        const marker = this.allMarkers.find(
            (marker) => marker.Latitude === coords[1] && marker.Longitude === coords[0]
        );
        const googleMapsURL = `https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`;
        const addressHTML = `
            <div class="location-popup">
                <strong>${marker.Name}</strong><br>
                <p>${marker['Address Line 1']} ${marker['Address Line 2'] ? ', ' + marker['Address Line 2'] : ''},${
            marker['City/Town']
        },${marker['Postal Code']}</p>
                <button class="button button--primary button--small button--full-width" onclick="window.open('${googleMapsURL}', '_blank')">${
            window.storeLocatorStrings.direction
        }</button>
            </div>
        `;

        this.currentPopup = new mapboxgl.Popup().setLngLat(coords).setHTML(addressHTML).addTo(this.map);

        this.currentPopup.on('close', function () {
            currentThis.removeSelectedMarker();
        });
    }

    closeAllStoreItems() {
        document.querySelectorAll('.store-item').forEach((storeItem) => {
            const content = storeItem.querySelector('.content');
            content.style.display = 'none';
            storeItem.classList.remove('active');
        });
    }

    toRadians(degrees) {
        return (degrees * Math.PI) / 180;
    }

    calculateDistance(coord1, coord2) {
        const R = 6371;
        const dLat = this.toRadians(coord2[1] - coord1[1]);
        const dLon = this.toRadians(coord2[0] - coord1[0]);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRadians(coord1[1])) *
                Math.cos(this.toRadians(coord2[1])) *
                Math.sin(dLon / 2) *
                Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        let distance = R * c;

        if (this.dataMapDistanceUnit === 'miles') {
            distance *= 0.621371;
        }

        return distance;
    }

    transferResults() {
        const sourceContainer = this.activeContainer;
        const targetContainer =
            sourceContainer === document.getElementById('search-results')
                ? document.getElementById('search-results-mobile')
                : document.getElementById('search-results');

        while (sourceContainer.firstChild) {
            targetContainer.appendChild(sourceContainer.firstChild);
        }
    }
}

customElements.define('store-locator', StoreLocator);
